import { useState } from "react";
import { Trash2, X, AlertTriangle, Loader2, FileDown } from "lucide-react";

export default function BatchActionBar({
  selectedCount = 0,
  totalCount = 0,
  onClearSelection,
  onSelectAll,
  onDeleteSelected,
  isDeleting = false,
  entityName = "items",
  onExportSelected,
  isExporting = false,
}) {
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  if (selectedCount === 0) return null;

  return (
    <>
      {/* Floating Bottom / Top Selection Bar */}
      <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-40 bg-gray-900/95 dark:bg-slate-800/95 text-white px-5 py-3.5 rounded-2xl shadow-2xl border border-white/10 backdrop-blur-md flex items-center gap-4 animate-slideUp">
        <div className="flex items-center gap-2 pr-3 border-r border-white/10">
          <span className="w-6 h-6 rounded-full bg-blue-500 text-xs font-black flex items-center justify-center text-white">
            {selectedCount}
          </span>
          <span className="text-xs font-semibold whitespace-nowrap">
            {selectedCount} {entityName} selected
          </span>
        </div>

        {totalCount > selectedCount && onSelectAll && (
          <button
            type="button"
            onClick={onSelectAll}
            className="text-xs text-blue-400 hover:text-blue-300 font-semibold underline whitespace-nowrap"
          >
            Select all {totalCount}
          </button>
        )}

        <div className="flex items-center gap-2">
          {onExportSelected && (
            <button
              type="button"
              onClick={onExportSelected}
              disabled={isExporting || isDeleting}
              className="px-4 py-2 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold flex items-center gap-1.5 transition-all shadow-md shadow-emerald-600/30 disabled:opacity-50"
            >
              {isExporting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FileDown className="w-3.5 h-3.5" />}
              Export PDF ({selectedCount})
            </button>
          )}

          <button
            type="button"
            onClick={() => setShowConfirmModal(true)}
            disabled={isDeleting || isExporting}
            className="px-4 py-2 rounded-xl bg-red-600 hover:bg-red-700 text-white text-xs font-bold flex items-center gap-1.5 transition-all shadow-md shadow-red-600/30 disabled:opacity-50"
          >
            {isDeleting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
            Delete Selected ({selectedCount})
          </button>

          <button
            type="button"
            onClick={onClearSelection}
            disabled={isDeleting}
            className="p-2 rounded-xl hover:bg-white/10 text-gray-400 hover:text-white transition-colors"
            title="Clear Selection"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showConfirmModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-fadeIn">
          <div className="bg-white dark:bg-slate-800 rounded-3xl border border-gray-200 dark:border-slate-700 p-6 max-w-md w-full shadow-2xl space-y-4">
            <div className="flex items-center gap-3 text-red-600 dark:text-red-400">
              <div className="p-3 rounded-2xl bg-red-100 dark:bg-red-500/20">
                <AlertTriangle className="w-6 h-6" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-gray-900 dark:text-white">Delete Selected {entityName}?</h3>
                <p className="text-xs text-gray-500 dark:text-gray-400">This action cannot be undone.</p>
              </div>
            </div>

            <p className="text-sm text-gray-600 dark:text-gray-300">
              Are you sure you want to permanently delete <strong>{selectedCount}</strong> selected {entityName}? All associated data will be removed.
            </p>

            <div className="flex items-center justify-end gap-3 pt-2">
              <button
                type="button"
                onClick={() => setShowConfirmModal(false)}
                disabled={isDeleting}
                className="px-4 py-2.5 rounded-xl border border-gray-200 dark:border-slate-700 text-gray-700 dark:text-gray-300 text-xs font-semibold hover:bg-gray-50 dark:hover:bg-slate-700"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={async () => {
                  setShowConfirmModal(false);
                  await onDeleteSelected();
                }}
                disabled={isDeleting}
                className="px-5 py-2.5 rounded-xl bg-red-600 hover:bg-red-700 text-white text-xs font-bold flex items-center gap-1.5 transition-all shadow-md shadow-red-600/30"
              >
                {isDeleting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                Yes, Delete {selectedCount}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
